import { apiRequest } from "./client";

function buildProductFormData(payload = {}, files = []) {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value) || (typeof value === "object" && value !== null)) {
      formData.append(key, JSON.stringify(value));
      return;
    }
    formData.append(key, typeof value === "boolean" ? String(value) : value);
  });

  (files || []).forEach((file) => {
    formData.append("images", file);
  });

  return formData;
}

function buildProductImportFormData(file) {
  const formData = new FormData();
  formData.append("sourceType", "upload");
  formData.append("file", file);
  return formData;
}

function normalizeImportId(importId) {
  const normalized = String(importId || "").trim();
  if (!normalized) throw new TypeError("importId es obligatorio para operar sobre un borrador de importación.");
  return encodeURIComponent(normalized);
}

export const productsApi = {
  list(projectId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/projects/${projectId}/products${query ? `?${query}` : ""}`);
  },

  lookupCode(projectId, code) {
    const query = new URLSearchParams({ code: String(code || "").trim() }).toString();
    return apiRequest(`/projects/${projectId}/products/lookup-code?${query}`);
  },

  bulkAdjustPrices(projectId, payload) {
    return apiRequest(`/projects/${projectId}/products/bulk-price-adjustment`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  createImport(projectId, source = {}) {
    if (source?.file) {
      return apiRequest(`/projects/${projectId}/products/imports`, {
        method: "POST",
        body: buildProductImportFormData(source.file),
      });
    }
    return apiRequest(`/projects/${projectId}/products/imports`, {
      method: "POST",
      body: JSON.stringify({
        sourceType: "google_sheets",
        url: String(source?.url || "").trim(),
      }),
    });
  },

  reinterpretImport(projectId, importId, payload) {
    const safeImportId = normalizeImportId(importId);
    return apiRequest(`/projects/${projectId}/products/imports/${safeImportId}/interpretation`, {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    });
  },

  previewImport(projectId, importId, payload) {
    const safeImportId = normalizeImportId(importId);
    return apiRequest(`/projects/${projectId}/products/imports/${safeImportId}/preview`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    });
  },

  commitImport(projectId, importId) {
    const safeImportId = normalizeImportId(importId);
    return apiRequest(`/projects/${projectId}/products/imports/${safeImportId}/commit`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  deleteImport(projectId, importId) {
    const safeImportId = normalizeImportId(importId);
    return apiRequest(`/projects/${projectId}/products/imports/${safeImportId}`, {
      method: "DELETE",
    });
  },

  // files: array de File (input type="file" multiple), opcional.
  // Si hay archivos, se manda como multipart/form-data (así lo espera Multer en el back).
  create(projectId, payload, files = [], options = {}) {
    if (files?.length) {
      return apiRequest(`/projects/${projectId}/products`, {
        method: "POST",
        body: buildProductFormData(payload, files),
        onUploadProgress: options.onUploadProgress,
        timeoutProfile: "upload",
      });
    }
    return apiRequest(`/projects/${projectId}/products`, {
      method: "POST",
      body: JSON.stringify(payload),
      timeoutProfile: "mutation",
    });
  },

  update(projectId, productId, payload, files = [], options = {}) {
    if (files?.length) {
      return apiRequest(`/projects/${projectId}/products/${productId}`, {
        method: "PUT",
        body: buildProductFormData(payload, files),
        onUploadProgress: options.onUploadProgress,
        timeoutProfile: "upload",
      });
    }
    return apiRequest(`/projects/${projectId}/products/${productId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
      timeoutProfile: "mutation",
    });
  },

  remove(projectId, productId, options = {}) {
    const mode = String(options?.mode || "").trim();
    const suffix = mode ? `?mode=${encodeURIComponent(mode)}` : "";
    return apiRequest(`/projects/${projectId}/products/${productId}${suffix}`, {
      method: "DELETE",
      timeoutProfile: "mutation",
    });
  },
};
